/**
 * StoryForge Recorder — Custom Capacitor Plugin Interface
 * Native: Android (Kotlin foreground service + MediaRecorder) / iOS (AVAudioRecorder)
 * Web fallback: existing MediaRecorder + IndexedDB from useRecorder.ts
 */

import { registerPlugin } from "@capacitor/core";

export type NativeRecorderState = "idle" | "recording" | "paused";

export interface StartRecordingOptions {
  projectId: string;
  sessionId: string;
  sampleRate?: number;  // default 48000
  bitRate?: number;     // default 64000
}

export interface StopRecordingResult {
  filePath: string;
  durationMs: number;
  format: string;       // "ogg" (Android) or "m4a" (iOS)
  mimeType: string;     // "audio/ogg" or "audio/mp4"
  sizeBytes: number;
}

export interface RecorderStatus {
  state: NativeRecorderState;
  durationMs: number;
  amplitude: number;    // 0.0–1.0
}

export interface OrphanedRecording {
  sessionId: string;
  filePath: string;
  durationMs: number;
  timestamp: number;
}

export interface StoryForgeRecorderPlugin {
  checkPermissions(): Promise<{ microphone: string }>;
  requestPermissions(): Promise<{ microphone: string }>;
  startRecording(options: StartRecordingOptions): Promise<void>;
  pauseRecording(): Promise<void>;
  resumeRecording(): Promise<void>;
  stopRecording(): Promise<StopRecordingResult>;
  discardRecording(): Promise<void>;
  getStatus(): Promise<RecorderStatus>;
  getOrphanedRecordings(): Promise<{ recordings: OrphanedRecording[] }>;
  deleteOrphanedRecording(options: { sessionId: string }): Promise<void>;
}

// Register plugin — native implementations auto-detected by Capacitor
// Web fallback is handled in useRecorder.ts (not here)
const StoryForgeRecorder = registerPlugin<StoryForgeRecorderPlugin>("StoryForgeRecorder");

export default StoryForgeRecorder;
